import { type UserListItem, type UserListQuery, type UserRole, USER_ROLES } from '@utazzo/shared';
import { UzAvatar, UzButton, type UzDropdownMenuItem } from '@utazzo/ui';
import { KeyRound, Network, ShieldOff, ShieldPlus, Trash2, UserCog } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ExitReleaseConfirmDialog,
  type ExitReleaseAction,
} from '../components/ExitReleaseConfirmDialog';
import { UserFormDrawer } from '../components/UserFormDrawer';
import { UserHierarchyDialog } from '../components/UserHierarchyDialog';
import {
  useBulkDeleteUsers,
  useBulkSetUsersStatus,
  useDeactivateUser,
  useDeleteUser,
  useReactivateUser,
  useSendPasswordReset,
  useUsersList,
} from '../use-users';
import { ROLE_LABELS, ROLE_TONES, UserSalesCoverageCell } from '../user-display';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen } from '@/components/list-screen';
import { SoftPill } from '@/components/soft-pill';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  ...USER_ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r })),
];

const FILTERS: ReadonlyArray<DataGridFilter> = [
  { id: 'role', label: 'Role', options: ROLE_OPTIONS, widthClass: 'w-48', multi: true },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

const COLUMNS: ReadonlyArray<DataGridColumn<UserListItem>> = [
  {
    id: 'fullName',
    header: 'User',
    sortable: true,
    cell: (row) => (
      <div className="flex items-center gap-3">
        <UzAvatar name={row.fullName} size="sm" />
        <div className="flex flex-col">
          <span className="font-medium text-zinc-900">{row.fullName}</span>
          <span className="text-xs text-zinc-500">{row.email}</span>
        </div>
      </div>
    ),
  },
  {
    id: 'phone',
    header: 'Phone',
    sortable: true,
    cell: (row) =>
      row.phone ? <span>{row.phone}</span> : <span className="text-zinc-400">-</span>,
  },
  {
    id: 'employeeCode',
    header: 'Employee code',
    sortable: true,
    cell: (row) =>
      row.employeeCode ? (
        <span className="font-mono text-xs">{row.employeeCode}</span>
      ) : (
        <span className="text-zinc-400">-</span>
      ),
  },
  {
    id: 'role',
    header: 'Role',
    sortable: true,
    cell: (row) => (
      <SoftPill className={ROLE_TONES[row.role] ?? 'bg-zinc-100 text-zinc-600'}>
        {ROLE_LABELS[row.role] ?? row.role}
      </SoftPill>
    ),
  },
  {
    id: 'branch',
    header: 'Branch',
    sortable: true,
    // Hidden by default to declutter; re-enable from the column menu.
    defaultHidden: true,
    cell: (row) => <span>{row.branch?.name ?? '-'}</span>,
  },
  {
    id: 'department',
    header: 'Department',
    sortable: true,
    defaultHidden: true,
    cell: (row) => <span>{row.department?.name ?? '-'}</span>,
  },
  {
    id: 'subBranch',
    header: 'Sub-branch',
    sortable: true,
    defaultHidden: true,
    cell: (row) => <span>{row.subBranch?.name ?? '-'}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => (
      <div className="flex flex-col items-start gap-1">
        {row.isActive ? (
          <SoftPill className="bg-emerald-50 text-emerald-700" dot dotClassName="bg-emerald-500">
            Active
          </SoftPill>
        ) : (
          <SoftPill className="bg-zinc-100 text-zinc-600" dot dotClassName="bg-zinc-400">
            Inactive
          </SoftPill>
        )}
        {row.mustResetPassword && (
          <SoftPill className="bg-amber-50 text-amber-700" dot dotClassName="bg-amber-500">
            Reset pending
          </SoftPill>
        )}
      </div>
    ),
  },
  {
    id: 'salesCoverage',
    header: 'Sales coverage',
    defaultHidden: true,
    cell: (row) => (
      <UserSalesCoverageCell
        salesScopes={row.salesScopes}
        salesHauls={row.salesHauls}
        salesTripTypes={row.salesTripTypes}
        destinations={row.destinations}
      />
    ),
  },
  {
    id: 'lastLogin',
    header: 'Last login',
    sortable: true,
    cell: (row) =>
      row.lastLogin ? (
        new Date(row.lastLogin).toLocaleString()
      ) : (
        <span className="text-zinc-400">Never</span>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    defaultHidden: true,
    cell: (row) => new Date(row.createdAt).toLocaleString(),
  },
];

export const UsersListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const sendReset = useSendPasswordReset();
  const deleteUser = useDeleteUser();
  const bulkDelete = useBulkDeleteUsers();
  const bulkSetStatusMut = useBulkSetUsersStatus();
  // Org-chart dialog state. Holds the row id whose hierarchy is being
  // viewed; null = dialog closed. The dialog query is keyed by id and
  // staleTime'd in useUserHierarchy so re-opening the same row is cheap.
  const [orgChartUserId, setOrgChartUserId] = useState<string | null>(null);
  // Lifecycle-release confirm dialog. Deactivate + Delete both route through
  // it so the operator sees how many leads will move to the Unassigned
  // queue before committing. `null` = closed.
  const [exitConfirm, setExitConfirm] = useState<{
    userId: string;
    userName: string;
    action: ExitReleaseAction;
  } | null>(null);

  // Extend the static column list with an "Organization" column that
  // dispatches into the dialog state. Built inside the component so the
  // cell render can close over `setOrgChartUserId`.
  const columns = useMemo(() => {
    const orgColumn: DataGridColumn<UserListItem> = {
      id: 'organization',
      header: 'Organization',
      width: '170px',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-zinc-600">
            {row.reportingManager ? (
              <>Reports to {row.reportingManager.fullName}</>
            ) : (
              <span className="text-zinc-400">No manager</span>
            )}
          </span>
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => setOrgChartUserId(row.id)}
            className="w-fit gap-1 px-1 text-[11px] text-violet-700 hover:text-violet-900"
          >
            <Network className="h-3.5 w-3.5" />
            View hierarchy
          </UzButton>
        </div>
      ),
    };
    return [...COLUMNS, orgColumn];
  }, []);

  return (
    <>
      <ListScreen<UserListItem, UserListQuery>
        title="Users"
        description="Manage who can sign in, what role they have, and whether they're active."
        useList={useUsersList}
        defaultSort={{ by: 'createdAt', dir: 'desc' }}
        // Columns are dense (name+email, sales coverage, hierarchy, etc.) -
        // use auto layout so each column grows to fit its content and the
        // outer overflow-x-auto handles horizontal scroll. Pairs with the
        // grid-wide word-wrap default so nothing gets clipped.
        tableLayout="auto"
        toQuery={(state, base) => {
          const status = state.filters.status;
          const roles = (state.filters.role ?? '')
            .split(',')
            .map((r) => r.trim())
            .filter(Boolean) as UserRole[];
          return {
            ...base,
            ...(roles.length > 0 ? { roles } : {}),
            ...(status === 'active' ? { isActive: true } : {}),
            ...(status === 'inactive' ? { isActive: false } : {}),
          };
        }}
        columns={columns}
        filters={FILTERS}
        getRowId={(row) => row.id}
        // Row clicks are surprising on this screen - cells host their
        // own targets (Hierarchy CTA, user name link) and editing is
        // explicit via the actions menu.
        disableRowClick
        createButton={{ label: 'New user' }}
        renderDrawer={({ open, mode, row, close }) => (
          <UserFormDrawer
            open={open}
            onOpenChange={(o) => {
              if (!o) close();
            }}
            mode={mode}
            user={row}
          />
        )}
        rowActions={{
          edit: true,
          extra: (row): UzDropdownMenuItem[] => [
            {
              type: 'item',
              label: 'View details',
              icon: <UserCog className="h-4 w-4" />,
              onSelect: () => navigate(`/users/${row.id}`),
            },
            {
              type: 'item',
              label: 'Send password reset',
              icon: <KeyRound className="h-4 w-4" />,
              onSelect: () => sendReset.mutate(row.id),
            },
            { type: 'separator' },
            row.isActive
              ? {
                  type: 'item',
                  label: 'Deactivate',
                  icon: <ShieldOff className="h-4 w-4" />,
                  disabled: row.id === me?.id,
                  // Route through the release-preview dialog instead of an
                  // immediate mutate: deactivating releases the user's leads,
                  // so the operator must see the count first.
                  onSelect: () =>
                    setExitConfirm({
                      userId: row.id,
                      userName: row.fullName,
                      action: 'deactivate',
                    }),
                }
              : {
                  type: 'item',
                  label: 'Reactivate',
                  icon: <ShieldPlus className="h-4 w-4" />,
                  onSelect: () => reactivate.mutate(row.id),
                },
            { type: 'separator' },
            {
              type: 'item',
              label: 'Delete user',
              icon: <Trash2 className="h-4 w-4" />,
              danger: true,
              disabled: row.id === me?.id,
              // Delete also releases leads; use the same preview dialog. The
              // built-in ListScreen delete confirm is sync and can't await
              // the async preview count, so it is intentionally not used.
              onSelect: () =>
                setExitConfirm({ userId: row.id, userName: row.fullName, action: 'delete' }),
            },
          ],
        }}
        bulkActions={{
          delete: {
            onDelete: async (rows) => {
              await bulkDelete.mutateAsync(rows.map((u) => u.id));
            },
            excludeRow: (row) => row.id === me?.id,
            confirm: (rows) => ({
              title: `Delete ${rows.length} ${rows.length === 1 ? 'user' : 'users'}?`,
              description:
                "The selected users will be soft-deleted and lose access immediately. Each user's Active (Proposal / Hot / Warm) and Not Qualified leads will move to the Unassigned queue, visible to their reporting manager and any admin. You can't delete yourself, so your own account is skipped if selected.",
            }),
          },
          setActive: {
            onSetActive: async (rows, isActive) =>
              bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
            excludeRow: (row) => row.id === me?.id,
            confirm: (rows, isActive) => ({
              title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} ${rows.length === 1 ? 'user' : 'users'}?`,
              description: isActive
                ? 'They will be able to sign in again.'
                : "They will be signed out and unable to access the admin until reactivated. Each user's Active (Proposal / Hot / Warm) and Not Qualified leads will move to the Unassigned queue, visible to their reporting manager and any admin. You can't deactivate yourself.",
            }),
          },
        }}
        copy={{
          searchPlaceholder: 'Search by name, email, phone, or employee code',
          emptyTitle: 'No users match these filters',
          emptyDescription: 'Adjust your role or status filters, or clear the search box.',
          errorTitle: 'Failed to load users',
          errorDescription: 'The server returned an error. Try refreshing or check your network.',
        }}
      />
      <UserHierarchyDialog
        open={orgChartUserId !== null}
        userId={orgChartUserId}
        onOpenChange={(o) => {
          if (!o) setOrgChartUserId(null);
        }}
      />
      <ExitReleaseConfirmDialog
        open={exitConfirm !== null}
        userId={exitConfirm?.userId ?? null}
        userName={exitConfirm?.userName ?? ''}
        action={exitConfirm?.action ?? 'deactivate'}
        onOpenChange={(o) => {
          if (!o) setExitConfirm(null);
        }}
        onConfirm={async () => {
          if (!exitConfirm) return;
          if (exitConfirm.action === 'delete') {
            await deleteUser.mutateAsync(exitConfirm.userId);
          } else {
            await deactivate.mutateAsync(exitConfirm.userId);
          }
        }}
      />
    </>
  );
};
